import React, { useEffect, useState, useCallback } from "react";
import { cls } from "@wss/cls";
import { upperCaseFirst } from "@wss/utilities/strings";
import formatAsCurrency from "@wss/utilities/formatAsCurrency";
import { GroupMember, Product } from "~/common/Store/models/ProductListing";
import { UserState } from "~/common/Store/models/RootReadonly";
import getVgItemPricing from "../getVgItemPricing";
import { getSearchParams } from "./logic";

interface Props {
  groupingValue: string;
  user: UserState;
  product: Product;
  isCompareWithProductBox?: boolean;
  isOnCompareProductsPage?: boolean;
  isCarouselProductBox?: boolean;
  isCustomizeNowProductBox?: boolean;
  hasCustomizableProductsOverride?: boolean;
  showError: boolean;
  handleGroupingChange: (
    event: React.ChangeEvent<HTMLSelectElement | HTMLInputElement>,
  ) => void;
}

interface VirtualGroupMember extends GroupMember {
  displayTitle: string | undefined;
  displayPriceInDropdownOptions: boolean;
  displaySalePrice: boolean;
  salePrice: number;
  nonSaleLowestPrice: number;
  sortIndex: number;
}

export default ({
  groupingValue,
  product,
  handleGroupingChange,
  isCompareWithProductBox,
  isOnCompareProductsPage,
  isCarouselProductBox,
  isCustomizeNowProductBox = false,
  hasCustomizableProductsOverride = false,
  showError,
  user,
}: Props) => {
  const { groupingAttributes } = product;

  const vgType =
    groupingAttributes.groupMembers[0]?.configurationOptions[0]?.name;

  const vgMembers = groupingAttributes.groupMembers.map(
    (groupMemberOriginal) => {
      const vgItemPrice = getVgItemPricing(groupMemberOriginal, user);

      const groupMember: VirtualGroupMember = {
        ...groupMemberOriginal,
        displayPriceInDropdownOptions: vgItemPrice.shouldDisplay,
        displayTitle: groupMemberOriginal.configurationOptions[0]?.value,
        sortIndex: groupMemberOriginal.configurationOptions[0]?.sortIndex ?? 1,
        salePrice: groupMemberOriginal.price.salePrice,
        nonSaleLowestPrice: vgItemPrice.lowestVisible,
        displaySalePrice: groupMemberOriginal.price.displaySalePrice,
        minimumCustomizationPrice:
          groupMemberOriginal.minimumCustomizationPrice,
      };

      return groupMember;
    },
  );

  const vgMembersSorted = vgMembers.sort((x, y) => x.sortIndex - y.sortIndex);

  const getMemberDisplayText = (member: VirtualGroupMember) => {
    const {
      displayPriceInDropdownOptions,
      displayTitle,
      isCustomizable,
      customizationUrl,
      customizationRequired,
      minimumCustomizationPrice,
      displaySalePrice,
      salePrice,
      nonSaleLowestPrice,
    } = member;

    if (!displayPriceInDropdownOptions) return displayTitle;
    if (
      (hasCustomizableProductsOverride && isCustomizable) ||
      (customizationRequired && (customizationUrl || isCustomizable))
    ) {
      return `${displayTitle} - ${formatAsCurrency.formatIfUndefined(
        minimumCustomizationPrice,
      )}`;
    }

    if (displaySalePrice && salePrice < nonSaleLowestPrice) {
      return `${displayTitle} - ${formatAsCurrency.formatIfUndefined(
        salePrice,
      )} Sale (reg. ${formatAsCurrency.formatIfUndefined(nonSaleLowestPrice)})`;
    }
    return `${displayTitle} - ${formatAsCurrency.formatIfUndefined(
      nonSaleLowestPrice,
    )}`;
  };

  const currentItem = isOnCompareProductsPage
    ? groupingValue || product.itemNumber
    : groupingValue;
  const [itemId, setItemId] = useState<string | undefined>(currentItem);

  const updateVGSelection = useCallback((): void => {
    const { items } = getSearchParams();

    if (!items || !isOnCompareProductsPage) return;

    const itemIds = items.split(",");
    const { groupMembers } = product.groupingAttributes;

    const groupMemberItemNumbers = new Set(
      groupMembers.map((member) => member.itemNumber),
    );
    const matchingItem = itemIds.find((item) =>
      groupMemberItemNumbers.has(item),
    );

    if (matchingItem) {
      setItemId(matchingItem);
    }
  }, [isOnCompareProductsPage, product.groupingAttributes, setItemId]);

  useEffect(() => {
    // always override w/groupingValue if available (can be updated on PLP)
    if (groupingValue !== itemId) {
      return setItemId(groupingValue);
    }

    updateVGSelection();
  }, [groupingValue, updateVGSelection, itemId]);

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>): void => {
    // compare page: update URL (without a refresh) because if we navigate away from the comapre
    // page (ex: to cart/checkout) and use back button to return - we want those same selected VG values to load
    if (isOnCompareProductsPage) {
      const { items } = getSearchParams();
      if (items && itemId) {
        const url = new URL(window.location.href);
        const newParams = items.replace(itemId, e.target.value);
        url.search = `?items=${newParams}`;
        window.history.replaceState({}, "", url);
      }
    }
    // fire the orginal callback either way
    handleGroupingChange(e);
  };

  return (
    <select
      value={itemId}
      className={cls(
        "suffix_dropdown",
        "configure",
        "item_number",
        isCustomizeNowProductBox && "ml-0 w-full",
        isOnCompareProductsPage && "mb-[8.5px]",
      )}
      style={{
        ...((isCompareWithProductBox ||
          isCarouselProductBox ||
          isOnCompareProductsPage) && {
          marginLeft: "-30px",
          width: `calc(100% + 30px)`,
        }),
        ...(showError && { borderColor: "#df7954" }),
      }}
      name="item_number"
      title={vgType}
      onChange={handleChange}
      data-testid="itemAddCartGrouping"
    >
      <option disabled value="">
        {}
        {upperCaseFirst(vgType!)}
      </option>
      {vgMembersSorted?.map((member) => (
        <option
          key={member.itemNumberId}
          value={member.itemNumber}
          data-testid="itemAddCartMember"
        >
          {getMemberDisplayText(member)}
        </option>
      ))}
    </select>
  );
};
